import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  getDocs,
  orderBy,
  serverTimestamp,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import firebaseApp, { db } from '@/config/firebase'; // Corrected: Use default import for firebase app
import { ColorPalette, CreateColorPalette, UpdateColorPalette } from '@shared/schemas/colorPalette';

const funcs = getFunctions(firebaseApp, 'europe-west2'); // Renamed to avoid conflict if functions is already imported

const managePalettes = httpsCallable<{
  action: 'list' | 'create' | 'update' | 'delete';
  paletteId?: string;
  payload?: Partial<CreateColorPalette> | Partial<UpdateColorPalette>;
}, { success: boolean; palettes?: ColorPalette[]; palette?: ColorPalette; paletteId?: string; error?: any }>(funcs, 'manageColorPalettes');

// Callable for simpleTestCallable
interface TestData { testPayload: string; }
interface TestResponse { message: string; receivedData: TestData; }

const callSimpleTest = httpsCallable<TestData, TestResponse>(funcs, 'simpleTestCallable');

export const simpleTestServiceCall = async (payload: string): Promise<TestResponse | null> => {
  try {
    const result = await callSimpleTest({ testPayload: payload });
    return result.data;
  } catch (error) {
    console.error("Error calling simpleTestCallable:", error);
    // Re-throw or handle as needed, could be FirebaseError with details
    throw error;
  }
};

// ===== DIRECT FIRESTORE ACCESS (NEW) =====

// Real-time subscription to palettes for a specific organization
export const subscribeToPalettes = (
  organizationId: string,
  callback: (palettes: ColorPalette[]) => void,
  onError?: (error: Error) => void
) => {
  // console.log('🔥 Setting up real-time palette subscription for org:', organizationId);
  
  const q = query(
    collection(db, 'color_palettes'),
    where('organizationId', 'in', [organizationId, 'system'])
    // Temporarily removed orderBy to avoid index requirement
    // orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(
    q, 
    (snapshot) => {
      
      const palettes = snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert Firestore Timestamps to Dates
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
          lastUsedAt: data.lastUsedAt instanceof Timestamp ? data.lastUsedAt.toDate() : data.lastUsedAt,
        } as ColorPalette;
      });
      
      callback(palettes);
    },
    (error) => {
      console.error('❌ Firestore palette subscription error:', error);
      if (onError) {
        onError(error);
      }
    }
  );
};

// Fallback: One-time fetch (for when subscription isn't needed)
export const fetchPalettes = async (organizationId: string): Promise<ColorPalette[]> => {
  console.log('📥 Fetching palettes once for org:', organizationId);
  
  try {
    const q = query(
      collection(db, 'color_palettes'),
      where('organizationId', 'in', [organizationId, 'system'])
      // Temporarily removed orderBy to avoid index requirement
      // orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const palettes = snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        lastUsedAt: data.lastUsedAt instanceof Timestamp ? data.lastUsedAt.toDate() : data.lastUsedAt,
      } as ColorPalette;
    });
    
    console.log('✅ Fetched palettes successfully, count:', palettes.length);
    return palettes;
  } catch (error) {
    console.error('❌ Failed to fetch palettes from Firestore:', error);
    throw error;
  }
};

// ===== LEGACY API ACCESS (KEPT FOR COMPARISON) =====

export const listPalettes = async (): Promise<ColorPalette[]> => {
  try {
    const result = await managePalettes({ action: 'list' });
    if (result.data.success && result.data.palettes) {
      return result.data.palettes;
    }
    console.error('Error listing palettes:', result.data.error);
    return [];
  } catch (error) {
    console.error('Firebase call failed (listPalettes):', error);
    throw error;
  }
};

export const createPalette = async (paletteData: CreateColorPalette): Promise<ColorPalette | null> => {
  try {
    const result = await managePalettes({ action: 'create', payload: paletteData });
    if (result.data.success && result.data.palette) {
      return result.data.palette;
    }
    console.error('Error creating palette:', result.data.error);
    return null;
  } catch (error) {
    console.error('Firebase call failed (createPalette):', error);
    throw error;
  }
};

export const updatePalette = async (paletteId: string, paletteData: UpdateColorPalette): Promise<boolean> => {
  try {
    const result = await managePalettes({ action: 'update', paletteId, payload: paletteData });
    return result.data.success;
  } catch (error) {
    console.error('Firebase call failed (updatePalette):', error);
    throw error;
  }
};

export const deletePalette = async (paletteId: string): Promise<boolean> => {
  try {
    const result = await managePalettes({ action: 'delete', paletteId });
    return result.data.success;
  } catch (error) {
    console.error('Firebase call failed (deletePalette):', error);
    throw error;
  }
}; 